/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { PlateLine } from '@1po/1po-bff-fe-spec/generated/catalog/easy_basket/response/GetEasyBasketOperationDetailResponse';
import { RootState } from 'app/AppStore';
import { ArrowLeftIcon } from 'assets/icons';
import {
  fetchPlateReferencesRequestSaga,
  getLastSearchedVehicleKey,
  getPlateReferences,
} from 'domains/catalog/Catalog.store';
import { MultiPlate, PlateImage } from 'domains/catalog/Catalog.types';
import PlateReferenceCardsContainer from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/PartsSelection/PlateModal/PlateReferenceCardsContainer';
import PlateDetailCard from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/PlateDetailCard';
import PlateThumbnailSection from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/PlateThumbnailSection';
import { SelectedIndexType } from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/SparePartsSection';
import { Box, Flex, Icon, MarginBox, Modal, Text } from 'UI';
import { SButton } from 'UI/Button/BackButton.styled';
import { getData, useExtraExtraLarge } from 'utils';
import { svgElementPlateId } from 'utils/svg/common';

interface PlateProps {
  selectedPlateLine: PlateLine;
  plateLines: PlateLine[];
  handleClose: () => void;
  handleSelectPlateLine: (plateIds: string[]) => void;
}

const Plate = ({ selectedPlateLine, handleSelectPlateLine, plateLines, handleClose }: PlateProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const xLarge = useExtraExtraLarge();
  const [isPlateModalOpen, setIsPlateModalOpen] = useState(false);
  const { plate, index } = selectedPlateLine;
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const plateImage = { plateId: plate.id, imageKey: plate.imageKey } as PlateImage;
  const modalWidth = window.innerHeight / window.innerWidth > 0.9 ? '90vh' : '100vh';

  const plateReferences = useSelector((state: RootState) =>
    getPlateReferences(state, { plateId: plate.id, vehicleKey }),
  );

  useEffect(() => {
    if (vehicleKey && !getData(plateReferences)) {
      dispatch(
        fetchPlateReferencesRequestSaga({
          plateId: plate.id,
          vehicleKey,
        }),
      );
    }
  }, [vehicleKey, dispatch, plate.id, plateReferences]);

  const [highlightedIndex, setHighlightedIndex] = useState<string | null>(index ?? null);
  const [selectedIndex, setSelectedIndex] = useState<SelectedIndexType>(index ?? null);

  const multiplates = plateLines.map((plateLine) => ({ plateIds: [plateLine.plate.id] } as MultiPlate));

  const handleExpand = () => {
    setIsPlateModalOpen(true);
  };

  return (
    <Box>
      <MarginBox mt={20} mb={50}>
        <SButton direction={'row'} align={'center'} justify={'flex-start'} onClick={handleClose}>
          <Icon IconComponent={ArrowLeftIcon} size={14} mr={10} mb={1} />
          <Text disableGutter type="heading4" cursor={'pointer'}>
            {t('catalog.easy_basket.plate_display.return', 'Back to Easy Basket selection')}
          </Text>
        </SButton>
      </MarginBox>

      <Flex direction={xLarge ? 'row' : 'column'} gap={xLarge ? 0 : 30}>
        <Flex>
          <Flex>
            {plateImage && (
              <Flex direction={'column'} gap={20}>
                <PlateDetailCard
                  plate={plateImage}
                  plateReferences={plateReferences}
                  setSelectedIndex={setSelectedIndex}
                  highlightedIndex={highlightedIndex}
                  setHighlightedIndex={setHighlightedIndex}
                  svgElementPlateId={svgElementPlateId}
                />
                <Flex size={0}>
                  <Text type="base" color="clear_blue" hoverUnderLine cursor={'pointer'} onClick={handleExpand}>
                    {t('catalog.plate.actions.see_full_plate', 'See full plate {{plateId}}', { plateId: plate.id })}
                  </Text>
                </Flex>
              </Flex>
            )}
            {plateImage && (
              <div id={'plate_popin_modal'}>
                <Modal
                  open={isPlateModalOpen}
                  footer={''}
                  closable
                  onCancel={() => setIsPlateModalOpen(false)}
                  width={'auto'}
                  getContainer={document.getElementById('plate_popin_modal') ?? document.body}
                >
                  <MarginBox mt={20} />
                  <Flex minWidth={modalWidth} maxWidth={modalWidth}>
                    <PlateDetailCard
                      plate={plateImage}
                      plateReferences={plateReferences}
                      setSelectedIndex={setSelectedIndex}
                      highlightedIndex={highlightedIndex}
                      setHighlightedIndex={setHighlightedIndex}
                      svgElementPlateId={svgElementPlateId}
                      setIsPlateModalOpen={setIsPlateModalOpen}
                    />
                  </Flex>
                </Modal>
              </div>
            )}
          </Flex>
          <PlateThumbnailSection
            multiplates={multiplates}
            handleSelectMultiplate={handleSelectPlateLine}
            plateImage={plateImage}
            selectedPlateId={selectedPlateLine.plate.id}
            isEasyBasket
          />
        </Flex>
        <PlateReferenceCardsContainer
          plateId={plate.id}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          setHighlightedIndex={setHighlightedIndex}
        />
      </Flex>
    </Box>
  );
};

export default Plate;
